
  import { defineComponent, PropType, ref } from 'vue';
  import VChart from 'vue-echarts';
  import type { EChartsOption, SeriesOption } from 'echarts';
  import { LineChart } from 'echarts/charts';
  import { GridComponent, LegendComponent, TitleComponent, TooltipComponent } from 'echarts/components';
  import { use } from 'echarts/core';
  import { CanvasRenderer } from 'echarts/renderers';

  import text from '@/common/mixins/text';
  import type { TDateFormats } from '@/common/types/date';

  import { useAdaptiveGridBottom } from './composables/useAdaptiveGridBottom';
  import { CHART_COLORS, ECHARTS_LEGEND_OPTIONS, ECHARTS_LOADING_OPTIONS } from './constants';
  import EChartsBase from './EChartsBase.vue';
  import EChartsScrollContainer from './EChartsScrollContainer.vue';
  import type { TEChartsLoadingOptions, TEChartsTitleConfig } from './types';
  import {
    buildCardAxisTooltipHtml,
    createTooltipPositioning,
    getEChartsGridOptionsByTitle,
    normalizeEChartsLabel,
  } from './utils';

  type TLineSeriesItem = {
    name: string;
    data: (number | string | [string | number, number] | { x: string | number; y: number })[];
    color?: string;
    smooth?: boolean;
    fillArea?: boolean;
    areaOpacity?: number;
    areaColor?: string;
  };

  type TStepMode = false | 'start' | 'middle' | 'end';
  type TXAxisType = 'category' | 'time';

  use([GridComponent, LegendComponent, TitleComponent, TooltipComponent, LineChart, CanvasRenderer]);

  export default defineComponent({
    name: 'EChartsLine',
    components: {
      EChartsCore: VChart,
      EChartsBase,
      EChartsScrollContainer,
    },
    mixins: [text],
    props: {
      xData: {
        type: Array as PropType<(string | number)[]>,
        default: () => [],
      },
      yData: {
        type: Array as PropType<(number | string)[]>,
        default: () => [],
      },
      series: {
        type: Array as PropType<TLineSeriesItem[]>,
        default: () => [],
      },
      lineName: {
        type: String,
        default: '',
      },
      lineColor: {
        type: String,
        default: '#73EA48',
      },
      stepMode: {
        type: String as PropType<Exclude<TStepMode, false>>,
      },
      xAxisType: {
        type: String as PropType<TXAxisType>,
        default: 'category',
      },
      timeValueFormat: {
        type: String as PropType<TDateFormats>,
      },
      showEmphasisValues: {
        type: Boolean,
        default: false,
      },
      smoothLines: {
        type: Boolean,
        default: false,
      },
      width: {
        type: Number,
      },
      height: {
        type: Number,
        default: 434,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      titleConfig: {
        type: Object as PropType<TEChartsTitleConfig>,
      },
      valueFractionDigits: {
        type: Number,
      },
      tooltipValueFractionDigits: {
        type: Number,
      },
      axisValueFractionDigits: {
        type: Number,
      },
      valuePostfix: {
        type: String,
        default: '',
      },
      tooltipPostfix: {
        type: String,
      },
      axisPostfix: {
        type: String,
      },
      formatLabelFunction: {
        type: Function as unknown as PropType<((value: number | string) => string) | null>,
        default: null,
      },
      formatValueFunction: {
        type: Function as unknown as PropType<((value: number | string) => string) | null>,
        default: null,
      },
      yAxisScale: {
        type: Boolean,
        default: true,
      },
    },
    setup() {
      const chartRef = ref<InstanceType<typeof VChart> | null>(null);
      const { gridBottom } = useAdaptiveGridBottom({ chartRef });
      const tooltipPositioning = createTooltipPositioning();

      return {
        chartRef,
        gridBottom,
        tooltipPositioning,
      };
    },
    computed: {
      getIsEmpty(): boolean {
        return this.getSeriesData.length === 0 && !this.isLoading;
      },
      getSeriesData(): TLineSeriesItem[] {
        if (this.series.length > 0) {
          return this.series.map((seriesItem) => ({
            ...seriesItem,
            name: normalizeEChartsLabel(seriesItem.name),
          }));
        }

        if (!this.yData?.length) return [];

        return [
          {
            name: normalizeEChartsLabel(this.lineName),
            data: this.yData,
            color: this.lineColor,
          },
        ];
      },
      getNormalizedStepMode(): TStepMode {
        return this.stepMode ?? false;
      },
      getFormattedTimeValue() {
        return (value: unknown): string => {
          if (value == null) return '';

          const stringValue = String(value);

          if (!this.timeValueFormat) return stringValue;

          return this.$momentFormat?.(value, this.timeValueFormat) ?? stringValue;
        };
      },
      getNormalizedNumericValue() {
        return (value: unknown): number => {
          const parsedValue = Number(value);
          return Number.isFinite(parsedValue) ? parsedValue : 0;
        };
      },
      getTooltipValueFractionDigits(): number {
        return this.tooltipValueFractionDigits ?? this.valueFractionDigits ?? 0;
      },
      getAxisValueFractionDigits(): number {
        return this.axisValueFractionDigits ?? this.valueFractionDigits ?? 0;
      },
      getTooltipPostfix(): string {
        return this.tooltipPostfix ?? this.valuePostfix;
      },
      getAxisPostfix(): string {
        return this.axisPostfix ?? this.valuePostfix;
      },
      getFormattedNumber() {
        return (value: unknown, fractionDigits: number): string => {
          if (value == null) return '';

          return this.formatNumber(this.getNormalizedNumericValue(value), fractionDigits, ' ', ',');
        };
      },
      getFormattedNumericValue() {
        return (value: unknown): string => {
          if (value == null) return '';

          const formattedValue = this.formatValueFunction
            ? this.formatValueFunction(this.getNormalizedNumericValue(value))
            : this.getFormattedNumber(value, this.getTooltipValueFractionDigits);

          return `${formattedValue}${this.getTooltipPostfix}`;
        };
      },
      getFormattedAxisLabel() {
        return (value: unknown): string => {
          if (value == null) return '';

          if (this.formatLabelFunction) {
            return this.formatLabelFunction(this.getNormalizedNumericValue(value));
          }

          return `${this.getFormattedNumber(value, this.getAxisValueFractionDigits)}${this.getAxisPostfix}`;
        };
      },
      getChartOption(): EChartsOption {
        const xAxisLabelOptions = {
          hideOverlap: true,
          color: '#989898',
          fontFamily: 'Fira Sans',
          fontSize: 15,
          lineHeight: 16,
          margin: 18,
          formatter: (value: string | number) =>
            this.xAxisType === 'time' ? this.getFormattedTimeValue(value) : String(value),
        };
        const xAxisOption =
          this.xAxisType === 'time'
            ? {
                type: 'time' as const,
                axisLabel: xAxisLabelOptions,
              }
            : {
                type: 'category' as const,
                data: this.xData,
                boundaryGap: false,
                axisLabel: {
                  ...xAxisLabelOptions,
                  interval: 0,
                },
              };

        const seriesOption: SeriesOption[] = this.getSeriesData.map((seriesItem, index) => {
          const fallbackColor =
            this.getSeriesData.length === 1 ? this.lineColor : CHART_COLORS[index % CHART_COLORS.length];
          const seriesColor = seriesItem.color ?? fallbackColor ?? this.lineColor;

          return {
            name: seriesItem.name,
            type: 'line' as const,
            step: this.getNormalizedStepMode,
            smooth: seriesItem.smooth ?? this.smoothLines,
            legendHoverLink: false,
            data: seriesItem.data.map((value) => {
              if (Array.isArray(value)) return value;
              if (typeof value === 'object' && value !== null && 'x' in value && 'y' in value) {
                return [value.x, value.y];
              }
              return Number(value);
            }),
            symbol: 'circle',
            symbolSize: 4,
            itemStyle: {
              color: seriesColor,
            },
            lineStyle: {
              width: 4,
              color: seriesColor,
            },
            areaStyle: seriesItem.fillArea
              ? {
                  opacity: seriesItem.areaOpacity ?? 0.2,
                  color: seriesItem.areaColor ?? seriesColor,
                }
              : undefined,
            emphasis: this.showEmphasisValues
              ? {
                  focus: 'series' as const,
                  symbol: 'circle',
                  symbolSize: 8,
                }
              : undefined,
          };
        });

        return {
          tooltip: {
            trigger: 'axis',
            appendTo: this.tooltipPositioning.appendTo,
            position: this.tooltipPositioning.position,
            axisPointer: { type: 'line' },
            padding: 0,
            formatter: (params) => {
              return buildCardAxisTooltipHtml(params, {
                getHeader: (listParams) => {
                  const firstParam = listParams[0];
                  if (!firstParam) return '';
                  const axisValue = ('axisValue' in firstParam ? firstParam.axisValue : firstParam.name) ?? '';
                  return this.xAxisType === 'time' ? this.getFormattedTimeValue(axisValue) : String(axisValue);
                },
                getSeriesLabel: (param) => param.seriesName,
                formatValue: (param) => {
                  const rawValue = Array.isArray(param.value) ? param.value[1] : param.value;
                  return this.getFormattedNumericValue(rawValue);
                },
              });
            },
          },
          legend: {
            show: true,
            ...ECHARTS_LEGEND_OPTIONS,
          },
          grid: {
            ...getEChartsGridOptionsByTitle(this.titleConfig?.text),
            bottom: this.gridBottom,
            left: 0,
            right: 12,
          },
          xAxis: xAxisOption,
          yAxis: {
            type: 'value',
            scale: this.yAxisScale,
            splitLine: {
              show: true,
              lineStyle: {
                color: 'rgba(120, 120, 120, 0.1)',
              },
            },
            axisLabel: {
              color: '#989898',
              fontFamily: 'Fira Sans',
              fontSize: 15,
              lineHeight: 16,
              formatter: (value: number) => this.getFormattedAxisLabel(value),
            },
          },
          series: seriesOption,
        };
      },
      getLoadingOptions(): TEChartsLoadingOptions {
        return ECHARTS_LOADING_OPTIONS;
      },
    },
  });
